/**
 * Runtime configuration loaded from /config.json before the React app mounts.
 *
 * The same FE bundle ships to every environment; env-specific values
 * (API base URL, build metadata, feature flags) are written into /config.json
 * by the deploy pipeline. All OAuth credentials live server-side (BFF pattern);
 * the frontend holds no client IDs or redirect URIs.
 */

export type RuntimeConfig = {
  api: {
    baseUrl: string;
  };
  build: {
    version: string;
    commit: string;
  };
  /**
   * Optional. Google Analytics measurement id for THIS environment — the id
   * differs per environment, and the same bundle ships everywhere, so it cannot
   * live in the code. Absent means no tag is loaded at all, which is what keeps
   * localhost and unconfigured environments out of the analytics property.
   */
  analytics?: {
    measurementId?: string;
  };
  /**
   * Optional. When auth.devLogin is true, MSAL is skipped and a role-picker
   * "Dev Login" screen issues a synthetic session — for building/demoing
   * role-gated features without a BMO Entra test user. Written into config.json
   * ONLY for localhost + Dev; the QA and Prod pipelines never set it, so those
   * environments stay real-SSO-only.
   */
  /**
   * Optional. Where "Back to Smart form Dashboard" sends the user.
   *
   * An EXTERNAL url by design: spec §11 says advisors have no dashboard in this
   * app, and the router actively bounces a real advisor off /advisor/dashboard —
   * so an in-app link would be broken for exactly the audience the story is
   * written for. BMO hosts the page that lists the SmartForms; this points back
   * at it.
   *
   * For an ISAM advisor this is the ONLY exit from the profile menu — it replaces
   * Sign out entirely, because ending their session here would end the one the
   * dashboard still holds (see SmartFormHeader). So when it is absent the item is
   * rendered DISABLED rather than hidden: an empty dropdown is not a better
   * answer than a visibly unavailable one.
   *
   * ⚠ Not yet supplied by BMO — every pipeline writes it empty today. Per
   * environment, so it lands as config rather than a deploy.
   */
  smartFormDashboardUrl?: string;
  /**
   * BMO's Smart Forms library — the list of application forms an advisor picks from
   * (INFLOWS-486 #1: the Welcome step's Cancel goes back there).
   *
   * Per LANGUAGE, because BMO's two pages are genuinely different URLs, not one path with a
   * swapped locale segment — `/ca-en/advisor/smart-forms-library` vs
   * `/ca-fr/conseiller/repertoire-des-formulaires-intelligents-en-ligne`. Neither can be
   * derived from the other, and neither can be derived from `smartFormDashboardUrl`, so both
   * are configured outright.
   *
   * Per environment as well (UAT `gamxuat2.bmogam.com`, Prod `www.bmogam.com`), and the same
   * bundle ships everywhere — hence config rather than code. Absent for the active language,
   * Cancel keeps its in-app exit.
   */
  smartFormLandingUrl?: {
    en?: string;
    fr?: string;
  };
  /**
   * BMO's public Applications & Forms page — the list a CUSTOMER (public user) picks a form
   * from, and where the Welcome step's Cancel returns them.
   *
   * A separate key from {@link RuntimeConfig.smartFormLandingUrl}, not a reuse of it: the
   * advisor's Smart Forms library is a different BMO page behind a different audience's
   * navigation, and sending a customer there would hand them an advisor surface. Same reasons
   * as the advisor pair for being per-language (BMO's two paths are unrelated —
   * `/ca-en/advisor/resources/applications-and-forms/` vs
   * `/ca-fr/conseiller/ressources/demandes-et-formulaires/`) and per-environment (UAT
   * `gamxuat2.bmogam.com`, Prod `bmogam.com`) while one bundle ships everywhere.
   *
   * Absent for the active language, Cancel keeps its browser-history exit — see
   * `leaveToPreviousPage`.
   */
  publicFormLandingUrl?: {
    en?: string;
    fr?: string;
  };
  auth?: {
    devLogin?: boolean;
    /**
     * Optional. Where an Entra (originator / approver) user lands after signing out.
     *
     * An ABSOLUTE, EXTERNAL url, per-environment — dev, UAT and prod each point at a
     * different BMO landing page, and the same bundle ships to all three, so it cannot
     * live in the code. Written into config.json by the deploy pipeline.
     *
     * Absent → the in-app `/auth/logged-out` confirmation page, which is what every
     * environment did before this key existed. That fallback is the reason it is safe
     * to ship the plumbing ahead of the real URLs.
     *
     * NOT applied to ISAM (advisor) sessions — an advisor must not be handed a
     * staff landing page. Their destination is still open; see LogoutPage.
     */
    postLogoutRedirectUrl?: string;
  };
};

/**
 * A config value that is only ever used as a NAVIGATION TARGET, or null.
 *
 * Every url in config.json arrives from a CloudFormation parameter, and several ship
 * empty on purpose until BMO supplies the real value. Empty, a typo, a relative path
 * or a `javascript:` value must not become a navigation — so anything that is not an
 * absolute http(s) url is treated exactly as "not configured", which each caller
 * already has a safe answer for.
 */
export function navigableUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const { protocol } = new URL(raw);
    return protocol === 'https:' || protocol === 'http:' ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Where the Welcome step's Cancel sends an advisor: BMO's Smart Forms library in the advisor's
 * own language. Null when that language has no usable url, and the caller keeps its in-app exit.
 *
 * Deliberately does NOT fall back to `smartFormDashboardUrl`: the library sits at its own path
 * (`…/advisor/smart-forms-library`, and an unrelated one in French), so anything derived from the
 * dashboard url is a guess that lands on a 404 — strictly worse than the in-app exit.
 */
export function smartFormLandingUrl(config: RuntimeConfig, language: 'en' | 'fr'): string | null {
  return navigableUrl(config.smartFormLandingUrl?.[language]);
}

/**
 * Where the Welcome step's Cancel sends a PUBLIC customer: BMO's Applications & Forms page in the
 * language they are reading the form in. Null when that language has no usable url, and the caller
 * falls back to the browser-history exit it had before this key existed.
 *
 * Deliberately does NOT fall back to the advisor's landing page: that is a different audience's
 * page, and a wrong destination is worse here than no destination — going back is already a
 * working exit for how customers actually arrive.
 */
export function publicFormLandingUrl(config: RuntimeConfig, language: 'en' | 'fr'): string | null {
  return navigableUrl(config.publicFormLandingUrl?.[language]);
}

const CONFIG_PATH = '/config.json';

let cached: RuntimeConfig | undefined;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const res = await fetch(CONFIG_PATH, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load ${CONFIG_PATH}: HTTP ${res.status}`);
  }
  const json = (await res.json()) as RuntimeConfig;
  assertConfigShape(json);
  cached = json;
  return cached;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error(
      'getRuntimeConfig() called before loadRuntimeConfig() resolved. Call loadRuntimeConfig() in your app bootstrap.',
    );
  }
  return cached;
}

function assertConfigShape(c: unknown): asserts c is RuntimeConfig {
  if (!c || typeof c !== 'object') throw new Error('runtime config: not an object');
  const cfg = c as Partial<RuntimeConfig>;
  if (!cfg.api?.baseUrl) {
    throw new Error('runtime config: api.baseUrl missing');
  }
}
